import { useStore } from "@store/index";
import { useMemo } from "react";

/**
 * useAuth is the unified client adapter. We bypass the global import restrictions for useSession and useSignOut on this file.
 * Therefore, eslint is disabled for the imports of useSession and useSignout here, on purpose.
 */
// eslint-disable-next-line no-restricted-imports
import useSignOut, { authState } from "./useSignOut";
// eslint-disable-next-line no-restricted-imports
import { useSession, signIn } from "next-auth/react";
import {
    CANCEL_SOCIAL_REGISTRATION_ACTION,
    COMPLETE_SOCIAL_REGISTRATION_ACTION,
} from "./social-login/constants";

// A wrapper around next-auth useSession
export function useAuth() {
    const { data: rawSession, status, update } = useSession();
    const customer = useStore((state) => state.data.me.customer);
    const { signOut } = useSignOut();

    const isLoading = status === "loading" || authState.isSignOutInProgress;
    const isLoggedIn = status === "authenticated" && !!rawSession?.ucid;
    const isAnonymous = status === "authenticated" && !!rawSession?.anonymousId;

    const socialRegistrationPrefillData = rawSession?.socialRegistrationPrefillData;
    const requiresSocialRegistration = !!socialRegistrationPrefillData;

    const cancelSocialRegistration = () => update({ action: CANCEL_SOCIAL_REGISTRATION_ACTION });
    const completeSocialRegistration = () =>
        update({ action: COMPLETE_SOCIAL_REGISTRATION_ACTION });

    const session = useMemo(() => {
        if (!rawSession) return rawSession;
        if (isLoggedIn && customer) {
            return {
                ...rawSession,
                user: {
                    ...rawSession.user,
                    name: `${customer.firstName || ""} ${customer.lastName || ""}`.trim(),
                },
            };
        }
        return rawSession;
    }, [rawSession, isLoggedIn, customer?.firstName, customer?.lastName]);

    return {
        session,
        socialRegistrationPrefillData,
        requiresSocialRegistration,
        cancelSocialRegistration,
        completeSocialRegistration,
        isLoggedIn,
        isAnonymous,
        isLoading,
        status,
        signIn,
        signOut,
    };
}
